import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { cn } from '@/lib/utils'
import type { ZHourBucket } from '@/types'

interface HourlySalesChartProps {
  buckets: ZHourBucket[]
  isLoading?: boolean
  className?: string
}

interface HourPoint {
  hour: number
  gross: number
  orderCount: number
  isPeak: boolean
}

export function HourlySalesChart({ buckets, isLoading, className }: HourlySalesChartProps) {
  const { formatCurrency, currencyCode } = useCurrency()
  const { points, peak, totalGross } = useMemo(() => normalize(buckets), [buckets])

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Hourly sales</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">Net sales by hour of day</p>
          </div>
          {peak && totalGross > 0 && (
            <div
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-300',
                'dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-200',
              )}
            >
              <span className="text-violet-500/80 dark:text-violet-300/70">Peak</span>
              <span className="font-semibold">{formatHourLabel(peak.hour)}</span>
              <span className="text-violet-500/60 dark:text-violet-300/60">·</span>
              <span className="tabular-nums">{formatCurrency(peak.gross)}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : totalGross <= 0 ? (
          <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
            No sales recorded yet today.
          </div>
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={points}
                margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                barCategoryGap="18%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="hour"
                  ticks={[0, 3, 6, 9, 12, 15, 18, 21]}
                  tickFormatter={formatHourTick}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  interval={0}
                  tickMargin={8}
                />
                <YAxis
                  tickFormatter={(v: number) => compactCurrency(v, currencyCode)}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                />
                <ReTooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
                  content={(props) => (
                    <HourTooltip {...props} formatCurrency={formatCurrency} />
                  )}
                />
                <Bar dataKey="gross" radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false}>
                  {points.map((p) => (
                    <Cell
                      key={p.hour}
                      fill={
                        p.isPeak
                          ? 'hsl(var(--primary))'
                          : 'hsl(var(--muted-foreground) / 0.28)'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface HourTooltipProps {
  active?: boolean
  payload?: Array<{ payload?: HourPoint }>
  formatCurrency: (n: number) => string
}

function HourTooltip({ active, payload, formatCurrency }: HourTooltipProps) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0]?.payload
  if (!point) return null

  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <div className="font-medium">{formatHourRange(point.hour)}</div>
      <div className="mt-1 flex items-center gap-2 tabular-nums">
        <span className="text-muted-foreground">Net</span>
        <span className="font-semibold">{formatCurrency(point.gross)}</span>
      </div>
      <div className="flex items-center gap-2 tabular-nums">
        <span className="text-muted-foreground">Orders</span>
        <span className="font-semibold">{point.orderCount}</span>
      </div>
    </div>
  )
}

function normalize(buckets: ZHourBucket[]): {
  points: HourPoint[]
  peak: ZHourBucket | null
  totalGross: number
} {
  const filled: ZHourBucket[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    gross: 0,
    order_count: 0,
  }))
  let total = 0
  let max = 0
  let peak: ZHourBucket | null = null
  for (const b of buckets) {
    if (b.hour < 0 || b.hour > 23) continue
    filled[b.hour] = b
    total += b.gross
    if (b.gross > max) {
      max = b.gross
      peak = b
    }
  }
  const points: HourPoint[] = filled.map((b) => ({
    hour: b.hour,
    gross: b.gross,
    orderCount: b.order_count,
    isPeak: peak !== null && peak.hour === b.hour,
  }))
  return { points, peak, totalGross: total }
}

function formatHourTick(h: number): string {
  if (h === 0) return '12a'
  if (h === 12) return '12p'
  if (h < 12) return `${h}a`
  return `${h - 12}p`
}

function formatHourLabel(h: number): string {
  if (h === 0) return '12 AM'
  if (h === 12) return '12 PM'
  if (h < 12) return `${h} AM`
  return `${h - 12} PM`
}

function formatHourRange(h: number): string {
  const next = (h + 1) % 24
  return `${formatHourLabel(h)} – ${formatHourLabel(next)}`
}
